@if (state$ | async; as state) {
  <div class="page-header">
    <div class="page-title">{{ !state.root ? 'BOM' : `BOM — ${state.root.data.name}` }}</div>
  </div>

  @if (state.addNodeDropdown) { <!-- probably going to remove it due to animations -->
    <add-node-dropdown [openAt]="state.addNodeDropdown.position" (bomItemSelected)="onAddNodeBomItemSelected($event)" (closed)="onAddNodeDropdownClose()"></add-node-dropdown>
  }

  @if (state.contextMenu) {
    <context-menu [items]="contextMenuItems" [openAt]="state.contextMenu.position" (actionSelected)="onContextMenuAction($event)" (closed)="onContextMenuClose()"></context-menu>
  }

  <!-- The grid, the cards and their headers render from the route alone; inside each card the content or its
       shadows are chosen by state.loading, so nothing moves when the tree lands (0127) -->
  <div class="bom-tree-page-grid">
    <div class="tree-card card">
      <div class="card-header">DRZEWO</div>

      @if (state.loading) {
        <!-- one root and five children one indent in: every tree's first frame -->
        @for (node of shadowNodes; track node) {
          <div class="tree-shadow-node" [class.tree-shadow-node-child]="!$first">
            <div class="shadow" [class.shadow-short]="$odd"></div>
          </div>
        }
      } @else {
        <bom-tree-node [item]="state.root!" [selectedNodeId]="state.selectedNodeId"></bom-tree-node>
      }
    </div>
    <div class="summary-card card">
      <div class="card-header">PODSUMOWANIE</div>

      @if (state.loading) {
        <div class="shadow-line"><span class="shadow shadow-long"></span></div>
        <div class="shadow-line"><span class="shadow"></span></div>
        <div class="shadow-line"><span class="shadow shadow-short"></span></div>
      } @else {
        <tree-summary [items]="[selectedNode(state)]" [rootType]="selectedRootType(state)"></tree-summary>
      }
    </div>
    <div class="notes-card card">
      <div class="card-header">
        NOTATKI

        <div class="card-header-toolbar">
          <div class="card-header-icon" (click)="notes()?.toggleComposer()">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </div>
        </div>
      </div>

      @if (state.loading) {
        <!-- bom-notes is not instantiated until the tree exists, so the page draws its two notes -->
        <div class="shadow-note">
          <div class="shadow-note-header"><span class="shadow shadow-short"></span></div>
          <span class="shadow shadow-long"></span>
        </div>
        <div class="shadow-note">
          <div class="shadow-note-header"><span class="shadow shadow-short"></span></div>
          <span class="shadow shadow-long"></span>
        </div>
      } @else {
        <bom-notes [bomItemId]="selectedBomItemId(state)" (noteAdded)="onNoteAdded($event)"></bom-notes>
      }
    </div>
    <div class="bom-rail">
      <div class="selected-item-card card">
        <div class="card-header">WYBRANY ELEMENT</div>

        @if (state.loading) {
          <!-- the same face and property rows, with a shadow in every value's place; the labels are the layout -->
          <div class="selected-item-face">
            <div class="selected-item-heading">
              <div class="selected-item-name"><span class="shadow shadow-long"></span></div>
              <div class="selected-item-description"><span class="shadow"></span></div>
              <div class="selected-item-type"><span class="shadow shadow-short"></span></div>
            </div>

            <div class="shadow shadow-photo-face"></div>
          </div>

          <div class="item-properties">
            <div class="item-property">
              <div class="item-property-label">Numer części</div>
              <div class="shadow item-property-value"></div>
            </div>
            <div class="item-property">
              <div class="item-property-label">Struktura</div>
              <div class="shadow shadow-short item-property-value"></div>
            </div>
            <div class="item-property">
              <div class="item-property-label">Jednostka</div>
              <div class="shadow shadow-short item-property-value"></div>
            </div>
            <div class="item-property">
              <div class="item-property-label">Masa</div>
              <div class="shadow shadow-short item-property-value"></div>
            </div>
            <div class="item-property">
              <div class="item-property-label">Opis</div>
              <div class="shadow shadow-long item-property-value"></div>
            </div>
          </div>
        } @else {
          <!-- The item's properties. One field edits at a time, the tree node idiom: double-click opens,
               Enter (or picking from a select) saves that one field, Escape cancels -->
          <!-- Name and type on the left, the main photo on the right; the photo opens full size in a new tab
               (the ZDJĘCIA row idiom) and is never an edit target. Without a photo the slot keeps its size -->
          <div class="selected-item-face">
            <div class="selected-item-heading">
              <div class="selected-item-name item-property" (dblclick)="startAttributeEdit('name')">
                @if (isEditing(state, 'name')) {
                  <input class="item-input" #attributeInput [value]="selectedNode(state).data.name"
                    (keyup.enter)="saveAttribute('name', attributeInput.value)" (keyup.escape)="cancelAttributeEdit()">
                } @else {
                  {{ selectedNode(state).data.name }}
                }
              </div>
              <!-- the description at a glance (0096); the Opis row below stays its editor -->
              @if (selectedNode(state).data.description; as description) {
                <div class="selected-item-description" [title]="description">{{ description }}</div>
              }
              <div class="selected-item-type">{{ typeLabels[selectedRootType(state)] }}</div>
            </div>

            @if (selectedNode(state).data.primaryPhotoAttachmentId; as photoId) {
              <a [href]="photoContentUrl(photoId)" target="_blank" rel="noopener">
                <item-photo size="face" [attachmentId]="photoId" [label]="selectedNode(state).data.name"></item-photo>
              </a>
            } @else {
              <item-photo size="face" [label]="selectedNode(state).data.name"></item-photo>
            }
          </div>

          <div class="item-properties">
            <div class="item-property" (dblclick)="startAttributeEdit('partNumber')">
              <div class="item-property-label">Numer części</div>
              @if (isEditing(state, 'partNumber')) {
                <input class="item-input" #attributeInput [value]="selectedNode(state).data.partNumber ?? ''"
                  (keyup.enter)="saveAttribute('partNumber', attributeInput.value)" (keyup.escape)="cancelAttributeEdit()">
              } @else {
                <div class="item-property-value text-mono">{{ selectedNode(state).data.partNumber ?? '—' }}</div>
              }
            </div>

            <div class="item-property" (dblclick)="startAttributeEdit('structure')">
              <div class="item-property-label">Struktura</div>
              @if (isEditing(state, 'structure')) {
                <select class="item-input" #attributeInput
                  (change)="saveAttribute('structure', attributeInput.value)" (keyup.escape)="cancelAttributeEdit()">
                  @for (structure of structures; track structure) {
                    <option [value]="structure" [selected]="structure === selectedNode(state).data.structure">{{ structureLabels[structure] }}</option>
                  }
                </select>
              } @else {
                <div class="item-property-value">{{ structureLabels[selectedNode(state).data.structure] }}</div>
              }
            </div>

            <div class="item-property" (dblclick)="startAttributeEdit('unit')">
              <div class="item-property-label">Jednostka</div>
              @if (isEditing(state, 'unit')) {
                <select class="item-input" #attributeInput
                  (change)="saveAttribute('unit', attributeInput.value)" (keyup.escape)="cancelAttributeEdit()">
                  @for (unit of units; track unit.code) {
                    <option [value]="unit.code" [selected]="unit.code === selectedNode(state).data.unit">{{ unit.label }}</option>
                  }
                </select>
              } @else {
                <div class="item-property-value" [title]="unitLabels[selectedNode(state).data.unit]">{{ selectedNode(state).data.unit }}</div>
              }
            </div>

            <div class="item-property" (dblclick)="startAttributeEdit('massKg')">
              <div class="item-property-label">Masa</div>
              @if (isEditing(state, 'massKg')) {
                <input class="item-input" #attributeInput type="text" inputmode="decimal" [value]="selectedNode(state).data.massKg != null ? formatAmountInput(selectedNode(state).data.massKg!) : ''"
                  (keyup.enter)="saveAttribute('massKg', attributeInput.value)" (keyup.escape)="cancelAttributeEdit()">
              } @else if (selectedNode(state).data.massKg) {
                <div class="item-property-value">{{ formatAmount(selectedNode(state).data.massKg!, 'kg') }}</div>
              } @else {
                <div class="item-property-value">—</div>
              }
            </div>

            <div class="item-property" (dblclick)="startAttributeEdit('description')">
              <div class="item-property-label">Opis</div>
              @if (isEditing(state, 'description')) {
                <input class="item-input" #attributeInput [value]="selectedNode(state).data.description ?? ''"
                  (keyup.enter)="saveAttribute('description', attributeInput.value)" (keyup.escape)="cancelAttributeEdit()">
              } @else {
                <div class="item-property-value" [title]="selectedNode(state).data.description ?? ''">{{ selectedNode(state).data.description ?? '—' }}</div>
              }
            </div>
          </div>

          @if (state.attributeEditError) {
            <div class="item-property-error">{{ state.attributeEditError }}</div>
          }
        }
      </div>
      <div class="attachments-card card">
        <div class="card-header">
          ZAŁĄCZNIKI

          <div class="card-header-toolbar">
            <div class="card-header-icon" (click)="attachmentsFileInput.click()">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
              </svg>
            </div>
          </div>
        </div>

        <input #attachmentsFileInput type="file" hidden [accept]="attachmentAccept" (change)="attachments()?.onFilePicked($event)">
        @if (state.loading) {
          <!-- the three tabs are the card's layout and stand before the data; their counts come with it -->
          <div class="shadow-tabs">
            <div class="shadow-tab">ZDJĘCIA</div>
            <div class="shadow-tab">DOKUMENTY</div>
            <div class="shadow-tab">PLIKI</div>
          </div>
          <ng-container *ngTemplateOutlet="shadowRows"></ng-container>
        } @else {
          <bom-attachments [bomItemId]="selectedBomItemId(state)" (attachmentsChanged)="onAttachmentsChanged($event)"></bom-attachments>
        }
      </div>
      <div class="stages-card card">
        <div class="card-header">ETAPY</div>

        @if (state.loading) {
          <ng-container *ngTemplateOutlet="shadowRows"></ng-container>
        } @else {
          <bom-item-stages [bomItemId]="selectedBomItemId(state)" [parentBomItemId]="selectedParentBomItemId(state)"></bom-item-stages>
        }
      </div>
      <div class="where-used-card card">
        <div class="card-header">UŻYWANE W</div>

        @if (state.loading) {
          <ng-container *ngTemplateOutlet="shadowRows"></ng-container>
        } @else {
          <where-used [bomItemId]="selectedBomItemId(state)"></where-used>
        }
      </div>
      <div class="orders-card card">
        <div class="card-header">ZLECENIA</div>

        @if (state.loading) {
          <ng-container *ngTemplateOutlet="shadowRows"></ng-container>
        } @else {
          <bom-item-orders [bomItemId]="selectedBomItemId(state)"></bom-item-orders>
        }
      </div>
    </div>
  </div>

  <!-- two lines at the rail rows' height: ZAŁĄCZNIKI, ETAPY, UŻYWANE W and ZLECENIA while the tree loads; once it has,
       each component draws its own shadows for the time its request takes (0127) -->
  <ng-template #shadowRows>
    <div class="shadow-line"><span class="shadow shadow-long"></span></div>
    <div class="shadow-line"><span class="shadow"></span></div>
  </ng-template>
}
